import { DocumentPlusTypeKeys } from "@app/app-modules/documents-plus/documents-plus.model";
import { FileTypes } from "@app/shared/constants/file-types.constants";
import { AbstractAdapter } from "@app/shared/models/adapter.model";
import { Field } from "@app/shared/models/form-field.model";
import { Pagination, Sortable } from "@app/shared/models/server-pagination.model";

export enum viewStates {
  viewList
}

/**
 * Interface for In Basket list
 */
export interface InBasket {
  sortable: Sortable;
  pagination: Pagination;
  applicationId: Field;
  userIdFilter: Field;
  initialDateRange: Field;
  finalDateRange: Field;
  descriptionFilter: Field;
  attachedFileFilter: Field;
  indexFilter: Field;
  inbasketList: InBasketList[];
}

/**
 * Interface for In Basket list content
 */
export interface InBasketList {
  userId: string;
  uniqueId: string;
  dateReceived: Date | string;
  fileType: string;
  documentType: string;
  documentTypeDescription: string;
  description: string;
  pageCount: number;
  fileSize: number;
  attachedFile: boolean;
  isDocumentNew: boolean;
  index: string;

  isSelected?: boolean;
  appId?: string;
  _attachedFile: string;
}

export interface InBasketHistory {
  fileId: string;
  documentHistoryList: InBasketHistoryList[];
}

export interface InBasketHistoryList {
  id: string;
  index: string;
  documentType: string;
  createdDate: string | Date;
  createdUser: string;
}

/**
 * Interface for delete web service
 */
export interface InBasketDelete {
  uniqueId: string[];
}

/**
 * Interface for PDF url web service
 */
export interface InBasketView {
  uniqueId: string;
  url: string;
}

/**
 * Interface for assigning a document to a new user
 */
export interface InBasketAssignUser {
  documents: string[];
  newUserId: string;
}

export interface InBasketAttach {
  companyNumber: Field;
  index: Field;
  documents: string[];
  descriptionToUse: string;
}

export interface InBasketDocumentTypes {
  type: string;
  description: string;
}

export interface InBasketMaintenance {
  uniqueId: string;
  applicationId: Field;
  description: Field;
}

export interface InBasketCount {
  totalDocuments: number;
  totalUnreadDocuments: number;
}

export interface InBasketMerge {
  url: string;
  fileList: string[];
}

/**
 * Data adapter for In Basket List request
 */
export class InBasketListRequestAdapter extends AbstractAdapter<InBasket> {
  adapt(rawData: InBasket) {
    rawData.initialDateRange.value = this.dateService.isValidDate(rawData.initialDateRange.value as Date)
      ? this.dateService.dateToString(rawData.initialDateRange.value as string)
      : "";
    rawData.finalDateRange.value = this.dateService.isValidDate(rawData.finalDateRange.value as Date)
      ? this.dateService.dateToString(rawData.finalDateRange.value as string)
      : "";
    rawData.inbasketList = [];
    return rawData;
  }
}

/**
 * Data adapter for In Basket List response
 */
export class InBasketListResponseAdapter extends AbstractAdapter<InBasket> {
  adapt(rawData: InBasket) {
    rawData.initialDateRange.value = rawData.initialDateRange.value ? this.dateService.dateToString(rawData.initialDateRange.value as string) : "";
    rawData.finalDateRange.value = rawData.finalDateRange.value ? this.dateService.dateToString(rawData.finalDateRange.value as string) : "";

    rawData.inbasketList.map((e) => {
      e.dateReceived = this.dateService.timeStampToDate(e.dateReceived as string);
      switch (e.fileType) {
        case "audio":
        case FileTypes.WEBM:
        case FileTypes.MP3:
        case FileTypes.MP4:
        case FileTypes.M4A:
        case FileTypes.WAV:
        case FileTypes.WAV49: {
          e.fileType = this.i18nService.translate(`DOCUMENTSPLUS.LBL_${DocumentPlusTypeKeys.Audio}`);
          break;
        }
        default: {
          e.fileType = e.fileType.toUpperCase();
        }
      }

      e._attachedFile = e.attachedFile ? this.i18nService.translate("APP.LBL_YES") : this.i18nService.translate("APP.LBL_NO");
    });
    return rawData;
  }
}

/**
 * Data adapter for In Basket delete
 */
export class InBasketDeleteAdapter extends AbstractAdapter<InBasketDelete> {
  adapt(rawData: InBasketDelete) {
    return rawData;
  }
}

/**
 * Data adapter for In Basket Assign User
 */
export class InBasketAssignUserAdapter extends AbstractAdapter<InBasketAssignUser> {
  adapt(rawData: InBasketAssignUser) {
    return rawData;
  }
}

/**
 * Data adapter for In Basket attach
 */
export class InBasketAttachAdapter extends AbstractAdapter<InBasketAttach> {
  adapt(rawData: InBasketAttach) {
    return rawData;
  }
}

/**
 * Data adapter for In Basket doc types
 */
export class InBasketDocumentTypesAdapter extends AbstractAdapter<InBasketDocumentTypes[]> {
  adapt(rawData: any) {
    return rawData.documentTypeList;
  }
}

/**
 * Data adapter for In Basket maintenance
 */
export class InBasketMaintenanceAdapter extends AbstractAdapter<InBasketMaintenance> {
  adapt(rawData: InBasketMaintenance) {
    return rawData;
  }
}

/**
 * Data adapter for InBasket count
 */
export class InBasketCountAdapter extends AbstractAdapter<InBasketCount> {
  adapt(rawData: InBasketCount) {
    return rawData;
  }
}

/**
 * Data adapter for In Basket history list
 */
export class InBasketHistoryListAdapter extends AbstractAdapter<InBasketHistoryList[]> {
  adapt(rawData: InBasketHistory) {
    rawData?.documentHistoryList?.map((e) => {
      e.createdDate = this.dateService.timeStampToDate(e.createdDate as string);
    });
    return rawData?.documentHistoryList;
  }
}

/**
 * Data adapter for InBasket pdf merger
 */
export class InBasketMergedDocumentsAdapter extends AbstractAdapter<InBasketMerge> {
  adapt(rawData: InBasketMerge) {
    return rawData;
  }
}
